import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { useService } from "@xstate/react";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import AlarmIcon from "@material-ui/icons/Alarm";
import TimerIcon from "@material-ui/icons/Timer";
import { makeStyles, Theme } from "@material-ui/core/styles";
import clsx from "clsx";
import ArcProgress from "react-arc-progress";
import {
  ControlValidityColorCriterion,
  getControlValidityColor,
} from "@egis-smartcity/parking";

import { sToDhms } from "../../../utils";
import { TimerService } from "../../../core/timer";

const useStyles = makeStyles<
  Theme,
  { controlValidityColor: ReturnType<typeof getControlValidityColor> }
>((theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    position: "relative",
    width: "100%",
    height: "50px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    color: theme.palette.common.white,
    "& >*": {
      backgroundColor: theme.palette.primary.main,
    },
    "& >:first-child": {
      marginRight: 8,
    },
    "& >:last-child": {
      marginLeft: 8,
    },
    zIndex: theme.zIndex.appBar - 1,
  },
  separator: {
    height: "60%",
    borderLeft: "1px solid white",
    margin: `0 ${theme.spacing(1.5)}px`,
  },
  tooltip: {
    position: "absolute",
    top: "calc(50% - 10px)",
    left: "calc(50% - 10px)",
    padding: 4,
    backgroundColor: "white",
    borderRadius: "50%",
  },
  tooltipContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  validityIcon: {
    color: ({ controlValidityColor }) => {
      return controlValidityColor || "default";
    },
    width: 20,
    height: 20,
  },
  gaugeContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  icons: {
    width: 20,
    height: 20,
  },
}));

const msg = defineMessages({
  processingTimeInProgress: {
    id: "productivityAgentLeft.processingTimeInProgress.label",
    description: "Label of the remaning processing.",
    defaultMessage: "Remaining processing time",
  },
  validityTime: {
    id: "productivityAgentLeft.validityTime.label",
    description: "Label of the remaning time before control expire.",
    defaultMessage: "Validity delay",
  },
});

export type ProductivityAgentLeftProps =
  React.HTMLAttributes<HTMLDivElement> & {
    controlValidityColorCriteria?: ControlValidityColorCriterion[];
    processingTimerService: TimerService;
    validityTimerService: TimerService;
  };

/**
 * Display data about the agent activity tracking. If one of the props
 * controlCount or processingTimeInProgress
 * are missing, this component will stay in loading state,
 * displaying some ghost loaders.
 *
 * @param props - The ProductivityAgentLeft component props.
 */
function ProductivityAgentLeft(props: ProductivityAgentLeftProps) {
  const {
    className,
    controlValidityColorCriteria,
    processingTimerService,
    validityTimerService,
    ...other
  } = props;

  const [processingTimerState] = useService(processingTimerService);
  const [validityTimerState] = useService(validityTimerService);

  const controlValidityColor = React.useMemo(() => {
    if (validityTimerState.matches("new")) return;

    return getControlValidityColor(
      new Date(validityTimerState.context.end as number),
      controlValidityColorCriteria
    );
  }, [controlValidityColorCriteria, validityTimerState]);

  const { formatMessage: t } = useIntl();
  const classes = useStyles({ controlValidityColor });

  return (
    <div className={clsx(classes.root, className)} {...other}>
      <b>
        {processingTimerState.matches("new")
          ? "00:00:00"
          : sToDhms(
              (processingTimerState.context.duration as number) -
                (processingTimerState.context.elapsed as number)
            )}
      </b>
      <div className={classes.gaugeContainer}>
        <Tooltip
          className={classes.tooltip}
          title={
            <Typography className={classes.tooltipContent}>
              {t(msg.processingTimeInProgress)}
            </Typography>
          }
        >
          <TimerIcon color="primary" className={classes.icons} />
        </Tooltip>
        <ArcProgress
          progress={
            processingTimerState.matches("new")
              ? 0
              : (processingTimerState.context.elapsed as number) /
                (processingTimerState.context.duration as number)
          }
          size={40}
          thickness={5}
        />
      </div>
      <div className={classes.separator} />
      <div className={classes.gaugeContainer}>
        <Tooltip
          className={classes.tooltip}
          title={
            <Typography className={classes.tooltipContent}>
              {t(msg.validityTime)}
            </Typography>
          }
        >
          <AlarmIcon color="primary" className={classes.validityIcon} />
        </Tooltip>
        <ArcProgress
          progress={
            validityTimerState.matches("new")
              ? 0
              : (validityTimerState.context.elapsed as number) /
                (validityTimerState.context.duration as number)
          }
          size={40}
          thickness={5}
          fillColor={controlValidityColor || "default"}
        />
      </div>
      <b>
        {validityTimerState.matches("new")
          ? "00:00:00"
          : sToDhms(
              (validityTimerState.context.duration as number) -
                (validityTimerState.context.elapsed as number)
            )}
      </b>
    </div>
  );
}

export default ProductivityAgentLeft;
